import { ToastrService } from 'ngx-toastr';
import { State as ModalState} from './../../shared/modal/store';
import {Injectable} from '@angular/core';
import {Actions, Effect, ofType} from '@ngrx/effects';
import {catchError, exhaustMap, map, mergeMap, take, tap} from 'rxjs/operators';
import * as _ from 'lodash';
// Payload Types
import {BaseStateV2} from '../../core/base.state';
import {Service} from './service';
import {CoreStore} from '../../core/core.store';
import {combineLatest, Observable, of} from 'rxjs';
import {Action, select} from '@ngrx/store';
import {getProfile as userProfile} from '../../core/auth/store';
import {FormBaseService} from '../../shared/forms/services/formBase';

export const FEATURE_NAME     = 'USER';
export const RESOURCE: string = 'users';


const FeatureActionTypes = {
    Enable: `[${FEATURE_NAME}]` + " Enable",
    UpdateStatus: `[${FEATURE_NAME}]` + " UpdateStatus",
    SendPasswordResetLink: `[${FEATURE_NAME}]` + " SendPasswordResetLink",
    ...CoreStore.baseActionTypesBuilder(FEATURE_NAME)
};

class IFeatureActions extends CoreStore.Actions {

    Enable: Action | any;
    UpdateStatus: Action | any;
    SendPasswordResetLink: Action | any;

    constructor(featureName: string){
        super(featureName);
        this.buildFeatureActions();
    }

    buildFeatureActions(){
        this._types = _.merge(this._types, FeatureActionTypes);
        this.Enable = class implements Action {
            public readonly type: any = FeatureActionTypes.Enable;
            constructor(public payload: any) {}
        };
        this.UpdateStatus = class implements Action {
            public readonly type: any = FeatureActionTypes.UpdateStatus;
            constructor(public payload: any) {}
        };
        this.SendPasswordResetLink = class implements Action {
            public readonly type: any = FeatureActionTypes.SendPasswordResetLink;
            constructor(public payload: any) {}
        };
    }

}

export const FeatureActions: IFeatureActions     = new IFeatureActions(FEATURE_NAME);


export const initialState: CoreStore.State = CoreStore.initialState;

export function reducer(state = initialState, action: any): any {
    return CoreStore.reducer(state, action, FeatureActions)
}

export const selectors = CoreStore.selectors(FEATURE_NAME);

// Effects

@Injectable()
export class Effects {

    private _coreEffectsOptions: CoreStore.EffectsOptions = {
        FEATURE_NAME: FEATURE_NAME,
        Actions: new CoreStore.Actions(FEATURE_NAME),
        featureService: this._svc,
        actions$: this.actions$
    };

    @Effect()
    Load$ = CoreStore.Effects.Load(this._coreEffectsOptions,
        () => RESOURCE);

    @Effect()
    Create$ = CoreStore.Effects.Create(this._coreEffectsOptions,
        (obj) => 'users?username=' + obj.email + '&isExternal=' + obj.isExternal + '&bypassFirebase=' + obj.bypassFirebase
    ).pipe(
        map(data => {
            if(data.payload.exist === true) {
                this.modalState.setNewUserInformation = {
                    email: data.payload.username,
                    isExternal: data.payload.isExternal
                };
                this.modalState.navigation.select("BypassFirebaseConfirmationComponent");
                setTimeout(() => {
                    this.modalState.open();
                });
            }
            else if(data.payload.id) {
                this.toastrService.success('', 'New user has been created successfully.', {
                    timeOut: 3000
                });
            }
            return data;
        }),
        catchError(error => {
            this.toastrService.error(
                '<b>If the problem persists, please contact an administrator.</b>',
                'An error occured while creating a new user. Please try again.',
                {
                    closeButton: true,
                    disableTimeOut: true,
                    enableHtml: true
                }
            );
            return error;
        })
    );

    @Effect()
    UpdateStatus$ = this.actions$.pipe(
        ofType(FeatureActions.Types.UpdateStatus),
        map((action: any) => action.payload),
        exhaustMap((obj) =>
            this._coreEffectsOptions.featureService.doUpdateUser(obj, 'users/updateStatus')
                .pipe(
                    mergeMap(data => {
                        if(data.isEnable === true) {
                            this.toastrService.success('', 'User(s) has been enabled successfully.', {
                                timeOut: 3000
                            });
                        }
                        else if(data.isEnable === false) {
                            this.toastrService.success('', 'User(s) has been disabled successfully.', {
                                timeOut: 3000
                            });
                        }
                        return [
                            new FeatureActions.Load({}),
                            new FeatureActions.Success({})
                        ];
                    }),
                    catchError(error => {
                        this.toastrService.error(
                            '<b>If the problem persists, please contact an administrator.</b>',
                            'An error occured while updating the user status. Please try again.',
                            {
                                closeButton: true,
                                disableTimeOut: true,
                                enableHtml: true
                            }
                        );
                        return (of(new FeatureActions.Failure(error)));
                    }))
        )
    );

    @Effect()
    SendPasswordResetLink$ = this.actions$.pipe(
        ofType(FeatureActions.Types.SendPasswordResetLink),
        map((action: any) => action.payload),
        exhaustMap((obj) =>
            this._coreEffectsOptions.featureService.doUpdateUser(obj, 'users/resetPassword')
                .pipe(
                    mergeMap(data => {
                        this.toastrService.success('', 'The reset password email has been sent to the user(s).', {
                            timeOut: 3000
                        });
                        return [
                            new FeatureActions.Load({}),
                            new FeatureActions.Success({})
                        ];
                    }),
                    catchError(error => {
                        this.toastrService.error(
                            '<b>If the problem persists, please contact an administrator.</b>',
                            'An error occured while sending reset password email to the user(s). Please try again.',
                            {
                                closeButton: true,
                                disableTimeOut: true,
                                enableHtml: true
                            }
                        );
                        return (of(new FeatureActions.Failure(error)));
                    }))
        )
    );

    @Effect()
    ActionSuccess$ = CoreStore.Effects.ActionSuccess(this._coreEffectsOptions);

    @Effect()
    ActionFailure$ = CoreStore.Effects.ActionFailure(this._coreEffectsOptions);

    @Effect()
    SuccessReset$ = CoreStore.Effects.SuccessReset(this._coreEffectsOptions);

    @Effect()
    FlushOnLogout$ = CoreStore.Effects.FlushOnLogout(this._coreEffectsOptions);

    @Effect()
    DeleteSuccessReload$ = this.actions$.pipe(
        ofType
        (FeatureActionTypes.DeleteSuccess),
        exhaustMap(() => of(new FeatureActions.Load({})))
    )


    constructor( protected actions$: Actions<any>,  protected _svc: Service, public toastrService: ToastrService, public modalState: ModalState ) {}
}



@Injectable()
export class State extends BaseStateV2 {

    FEATURE_NAME: string = FEATURE_NAME;
    FeatureActions: IFeatureActions = FeatureActions;
    _selectors = selectors;

    user(userId: string) {
        return this.list
            .pipe(
                map(users => _.filter(users, u => u.id == userId))
            );
    }

    get usersWithOutOwner() {
        return combineLatest(
            this._store.pipe(select(userProfile)),
            this.list,
            (profile, list) => {
                return _.filter(list, (u) => u.id !== profile.id)
            }
        )
    }

    get userForm() {
        return this.list
            .pipe(
                take(1),
                map(users =>
                    (new FormBaseService()).initForm(
                        FormBaseService.createGenericForm(
                            _.map(users, (user) => user.id)
                        )
                    )
                ),
            )
    }

    async updateStatus(obj){
        this._store.dispatch(new this.FeatureActions.UpdateStatus(obj));
        return this.success
    }

    async resetPassword(user){
        this._store.dispatch(new this.FeatureActions.SendPasswordResetLink(user));
        return this.success
    }

}

